/**
 * The typed client for the support service's staff API (RidgeWill/platform#29).
 *
 * ── THE SAME SHAPE AS fleet-api.ts, FOR THE SAME REASON ─────────────
 *
 * Support is its own service on its own origin. It never sees the admin
 * session cookie; it verifies a bearer access token audienced at support
 * (`<issuer>/app/support`), which auth mints for this portal because
 * RidgeWill/auth#557 declared `org-admin -> support`. So this client sends
 * `Authorization: Bearer` with `credentials: 'omit'`, and takes the token as a
 * function so a test can hand it one. Where it comes from is `fleet-token.ts`
 * (`supportToken`).
 *
 * ── UNLIKE fleet-api, SUPPORT SAYS WHY ──────────────────────────────
 *
 * Its refusals are `{ error: { code, message } }` written for a person
 * (`NOT_STAFF`, `TICKET_NOT_FOUND`, a validation message), so they are shown
 * as they are rather than collapsed into one sentence.
 */
import type { TokenSource } from './fleet-api.js';

export const SUPPORT_URL = (import.meta.env.VITE_SUPPORT_URL ?? '').replace(/\/$/, '');

if (!SUPPORT_URL) {
  // Loud at load, like VITE_FLEET_URL: a blank base sends every request to this
  // app's own origin, which the static host answers with HTML.
  console.error('[org-admin] VITE_SUPPORT_URL is not set: the Support screens cannot reach the support service');
}

export class SupportError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'SupportError';
  }
}

async function readError(res: Response): Promise<SupportError> {
  const body = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
  const message = body?.error?.message || `The support service answered ${res.status} ${res.statusText}.`;
  return new SupportError(res.status, message, body?.error?.code);
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  token: TokenSource,
  fetchImpl: typeof fetch,
  body?: unknown,
): Promise<T> {
  const res = await fetchImpl(`${SUPPORT_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${await token()}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    // Never the cookie: support allows no credentials, so sending one would
    // fail CORS outright, and it has no use for it.
    credentials: 'omit',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) throw await readError(res);
  // A DELETE answers 204 with no body.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── Shapes, as support's routes/admin.ts writes them ───────────────────

export type SavedView =
  | 'all-open'
  | 'unassigned'
  | 'waiting-on-us'
  | 'waiting-on-user'
  /** A draft reply waiting for a person to send it (support#79). */
  | 'waiting-for-review'
  | 'needs-category'
  | 'resolved-today'
  | 'spam';
export const SAVED_VIEWS: readonly SavedView[] = [
  'all-open',
  'unassigned',
  'waiting-on-us',
  'waiting-on-user',
  'waiting-for-review',
  'needs-category',
  'resolved-today',
  'spam',
];

/** Which of our addresses a ticket arrived at (support#67). */
export type Inbox = 'SUPPORT' | 'PRIVACY' | 'LEGAL';

// ── Categories (support#68) ─────────────────────────────────────────────

/** A category as a ticket shows it: its name, and the names from the top down to it. */
export interface CategoryLabel {
  id: string;
  name: string;
  trail: string[];
}

/** One row of the tree, as `GET /admin/categories` lists it: flat, in outline order. */
export interface Category {
  id: string;
  name: string;
  parentId: string | null;
  depth: number;
  /** Product slugs it is offered on; empty is every product. */
  products: string[];
  /** Set on the seeded categories the automatic rules file into (`privacy`, `legal`, ...). */
  key: string | null;
  sortOrder: number;
  archivedAt: string | null;
  ticketsLast90Days: number;
}

export interface CategoryList {
  /** Whether the caller may add, rename, move or archive (support-admin, global-admin). */
  canEdit: boolean;
  categories: Category[];
}

export type CategorySource = 'RULE' | 'REQUESTER' | 'STAFF';

// ── Form mapping (support#68) ───────────────────────────────────────────

/** The support forms a mapping rule can be about. */
export type MappedForm = 'WEB_FORM' | 'ACCOUNT_PORTAL';
export const MAPPED_FORMS: readonly MappedForm[] = ['WEB_FORM', 'ACCOUNT_PORTAL'];
export const MAPPED_FORM_LABELS: Record<MappedForm, string> = {
  WEB_FORM: 'Public site form',
  ACCOUNT_PORTAL: 'Account portal',
};

/**
 * Which form(s) a rule's topic shows on, and which channel(s) it routes
 * (auth#672). Replaced `channel` on rules: both forms now load their topic
 * list from these rules, so adding a topic is one step and cannot miss its rule.
 */
export type ShowOn = MappedForm | 'BOTH';
export const SHOW_ON: readonly ShowOn[] = ['WEB_FORM', 'ACCOUNT_PORTAL', 'BOTH'];
export const SHOW_ON_LABELS: Record<ShowOn, string> = {
  WEB_FORM: 'Public form',
  ACCOUNT_PORTAL: 'Account portal',
  BOTH: 'Both',
};

/** The topic both forms always end with; it is never mapped to a list position. */
export const SOMETHING_ELSE = 'Something else';

/** One rule: a selection on a form, for a product or any, filed under a category. */
export interface CategoryRule {
  id: string;
  /** auth#672. Absent from a support older than it, which had `channel` instead. */
  showOn?: ShowOn;
  /** The pre-auth#672 field, read only as a fallback for `showOn`. */
  channel?: MappedForm;
  /** Order in the form's topic list, lowest first (auth#672). */
  position?: number;
  /** Null is every product. */
  product: string | null;
  /** The selection as the form shows it; null is any selection. */
  topic: string | null;
  /** Null while the topic shows on a form but staff have not mapped it yet (auth#672). */
  category: CategoryLabel | null;
  updatedAt: string;
}

export const showOnOf = (rule: Pick<CategoryRule, 'showOn' | 'channel'>): ShowOn =>
  rule.showOn ?? rule.channel ?? 'WEB_FORM';

/** Whether a rule's topic is listed on `form`. */
export const showsOn = (rule: Pick<CategoryRule, 'showOn' | 'channel'>, form: MappedForm): boolean => {
  const on = showOnOf(rule);
  return on === 'BOTH' || on === form;
};

/** Rules in list order: position, then topic, with a missing position last. */
export function byPosition<R extends Pick<CategoryRule, 'position' | 'topic'>>(rules: readonly R[]): R[] {
  return [...rules].sort(
    (a, b) =>
      (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) ||
      (a.topic ?? '').localeCompare(b.topic ?? ''),
  );
}

/**
 * The topic list a member sees on `form` for `product` (null: a product with no
 * rules of its own), as support's `GET /topics` builds it: product rules plus
 * any-product rules, in position order, each topic once, "Something else" last.
 * A rule with no topic (any selection) lists nothing.
 */
export function topicsFor(rules: readonly CategoryRule[], form: MappedForm, product: string | null): string[] {
  const listed = byPosition(
    rules.filter((r) => r.topic && showsOn(r, form) && (r.product === null || r.product === product)),
  )
    .map((r) => r.topic!)
    .filter((t) => t !== SOMETHING_ELSE);
  return [...new Set(listed), SOMETHING_ELSE];
}

/**
 * Moving a rule one place up or down the list, as the position writes it
 * needs. The whole list is renumbered 10, 20, 30... in its new order and
 * only the rules whose position changes are returned, so rules that share a
 * position (or have none yet) come out distinct after the first move.
 */
export function reorder(
  rules: readonly CategoryRule[],
  id: string,
  direction: 'up' | 'down',
): { rule: CategoryRule; position: number }[] {
  const order = byPosition(rules);
  const i = order.findIndex((r) => r.id === id);
  const j = direction === 'up' ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= order.length) return [];
  [order[i], order[j]] = [order[j]!, order[i]!];
  return order
    .map((rule, k) => ({ rule, position: (k + 1) * 10 }))
    .filter(({ rule, position }) => rule.position !== position);
}

/** A rule as the create/update body, for a write that changes only its position. */
export const ruleInput = (rule: CategoryRule, position = rule.position): CategoryRuleInput => ({
  showOn: showOnOf(rule),
  ...(position === undefined ? {} : { position }),
  product: rule.product,
  topic: rule.topic,
  categoryId: rule.category?.id ?? null,
});

/** A selection people actually made recently, and where it lands now. */
export interface MappedSelection {
  channel: MappedForm;
  product: string;
  topic: string | null;
  tickets: number;
  ruleId: string | null;
  category: CategoryLabel | null;
}

export interface CategoryRuleOverview {
  canEdit: boolean;
  windowDays: number;
  rules: CategoryRule[];
  selections: MappedSelection[];
}

export interface CategoryRuleInput {
  showOn: ShowOn;
  /** Left out to let support put a new rule last. */
  position?: number;
  product: string | null;
  topic: string | null;
  /** Null: the topic shows on the form before it files anywhere (auth#672). */
  categoryId: string | null;
}

/** `Something is broken › Upload or photos`. */
export const categoryText = (label: CategoryLabel) => label.trail.join(' › ');

/**
 * The tree as picker options, indented by depth, for one product. Leaves out
 * archived categories and ones not offered on that product, and any category
 * under one left out, so nothing appears without its parent.
 */
export function categoryOptions(categories: readonly Category[], product?: string): { value: string; label: string }[] {
  const shown = new Set<string>();
  const out: { value: string; label: string }[] = [];
  for (const c of categories) {
    if (c.archivedAt) continue;
    if (c.parentId && !shown.has(c.parentId)) continue;
    if (product && c.products.length > 0 && !c.products.includes(product)) continue;
    shown.add(c.id);
    // An em space per level: the open list keeps leading space, a plain one collapses.
    out.push({ value: c.id, label: `${'\u2003'.repeat(c.depth)}${c.name}` });
  }
  return out;
}

/**
 * `WAITING_FOR_REVIEW` means a draft reply is waiting for a person (support#79).
 * Only saving a draft sets it, so it is not in `TICKET_STATUSES`, which are the
 * ones a person may choose.
 */
export type TicketStatus = 'OPEN' | 'WAITING_ON_USER' | 'WAITING_ON_SUPPORT' | 'RESOLVED' | 'WAITING_FOR_REVIEW';
export const TICKET_STATUSES: readonly TicketStatus[] = ['OPEN', 'WAITING_ON_SUPPORT', 'WAITING_ON_USER', 'RESOLVED'];
export type TicketPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export const TICKET_PRIORITIES: readonly TicketPriority[] = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];
export type TicketFlag = 'NONE' | 'SUSPECTED_SPAM' | 'MERGED';

export interface Requester {
  userId: string | null;
  /** Null once the requester's identity was closed: the address is gone. */
  email: string | null;
  /** Set when the requester closed their identity. No reply is emailed after. */
  closedAt: string | null;
}

export interface QueueTicket {
  reference: string;
  /**
   * False on a ticket made from feedback whose sender asked not to be
   * contacted: no requester, internal notes only, nothing emailed. Optional
   * because a support older than support#45 does not send it.
   */
  contactAllowed?: boolean;
  subject: string;
  product: string;
  status: TicketStatus;
  priority: TicketPriority;
  flag: TicketFlag;
  channel: string;
  /** Absent from a support older than support#67. */
  inbox?: Inbox;
  /** Null while it needs a category (support#68). Absent from an older support. */
  category?: CategoryLabel | null;
  requester: Requester;
  assigneeUserId: string | null;
  openedAt: string;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  lastMessage: { direction: string; at: string; preview: string } | null;
}

export interface QueuePage {
  view: SavedView;
  hasMore: boolean;
  nextCursor: string | null;
  tickets: QueueTicket[];
}

export interface TicketMessage {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL_NOTE' | 'SYSTEM';
  trust: 'SESSION' | 'SIGNED_TOKEN' | 'STAFF' | 'UNVERIFIED';
  authorUserId: string | null;
  /** An agent account wrote it (org-admin#178). Absent from a support build older than support#76. */
  authorIsAgent?: boolean;
  authorEmail: string | null;
  at: string;
  body: string;
  hasOriginal: boolean;
  attachments: { id: string; filename: string; bytes: number; isDiagnosticLog: boolean; scan: string }[];
}

export interface TicketEvent {
  id: string;
  kind: string;
  at: string;
  actorUserId: string | null;
  actorIsAgent: boolean;
  detail: unknown;
}

/**
 * A reply waiting for review (support#79): usually the support agent's, sent
 * by a person or discarded. `canSend` is whether the signed-in person may send
 * it (a support admin, never an agent); support decides, the portal only shows.
 */
export interface TicketDraft {
  id: string;
  body: string;
  authorUserId: string;
  authorIsAgent: boolean;
  savedAt: string;
  canSend: boolean;
}

export interface TicketDetail {
  reference: string;
  /** See `QueueTicket.contactAllowed`. */
  contactAllowed?: boolean;
  subject: string;
  product: string;
  status: TicketStatus;
  priority: TicketPriority;
  flag: TicketFlag;
  channel: string;
  inbox?: Inbox;
  /** Null while it needs a category; staff cannot resolve it until it has one (support#68). */
  category?: CategoryLabel | null;
  categorySource?: CategorySource | null;
  requester: Requester;
  assigneeUserId: string | null;
  openedAt: string;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  device: Record<string, unknown> | null;
  tags: string[];
  messages: TicketMessage[];
  events: TicketEvent[];
  /** The pending draft, or null. Absent from a support older than support#79. */
  draft?: TicketDraft | null;
  /**
   * Whether the signed-in person may delete an attachment (auth#672): a person
   * with support, auth or global admin, never an agent. Support decides; absent
   * from an older support, which has no delete at all.
   */
  canDeleteAttachments?: boolean;
}

/** An attachment staff deleted, from its `ATTACHMENT_DELETED` event (auth#672). */
export interface DeletedAttachment {
  attachmentId: string;
  messageId: string | null;
  filename: string;
  at: string;
  actorUserId: string | null;
}

/**
 * The attachments deleted from a ticket, by the message they were on. The
 * attachment row is gone once deleted, so the event is the only record of
 * it; the thread shows it as "Deleted by NAME on DATE" in place of the file.
 * One without a message id (or with an unreadable detail) is left to the
 * History panel.
 */
export function deletedAttachments(events: readonly TicketEvent[]): Map<string, DeletedAttachment[]> {
  const out = new Map<string, DeletedAttachment[]>();
  for (const e of events) {
    if (e.kind !== 'ATTACHMENT_DELETED') continue;
    const d = (e.detail ?? {}) as Record<string, unknown>;
    if (typeof d.messageId !== 'string' || typeof d.attachmentId !== 'string') continue;
    const row: DeletedAttachment = {
      attachmentId: d.attachmentId,
      messageId: d.messageId,
      filename: typeof d.filename === 'string' ? d.filename : 'An attachment',
      at: e.at,
      actorUserId: e.actorUserId,
    };
    out.set(d.messageId, [...(out.get(d.messageId) ?? []), row]);
  }
  return out;
}

export interface QueueQuery {
  view: SavedView;
  q?: string;
  product?: string;
  assignee?: string;
  /** A category id: that category and everything under it. */
  category?: string;
  cursor?: string;
}

/** The query string for `GET /admin/tickets`, empty values left out. */
export function queueSearch(query: QueueQuery): string {
  const params = new URLSearchParams({ view: query.view });
  if (query.q) params.set('q', query.q);
  if (query.product) params.set('product', query.product);
  if (query.assignee) params.set('assignee', query.assignee);
  if (query.category) params.set('category', query.category);
  if (query.cursor) params.set('cursor', query.cursor);
  return `?${params}`;
}

export function supportApi(token: TokenSource, fetchImpl: typeof fetch = fetch) {
  const get = <T>(path: string) => request<T>('GET', path, token, fetchImpl);
  const post = <T>(path: string, body: unknown) => request<T>('POST', path, token, fetchImpl, body);
  const put = <T>(path: string, body: unknown) => request<T>('PUT', path, token, fetchImpl, body);
  const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, token, fetchImpl, body);
  const del = (path: string) => request<void>('DELETE', path, token, fetchImpl);
  const ticket = (reference: string) => `/admin/tickets/${encodeURIComponent(reference)}`;
  const fb = (id: string) => `/admin/feedback/${encodeURIComponent(id)}`;

  return {
    queue: (query: QueueQuery) => get<QueuePage>(`/admin/tickets${queueSearch(query)}`),
    counts: () => get<{ counts: Record<SavedView, number> }>('/admin/tickets/counts'),
    ticket: (reference: string) => get<TicketDetail>(ticket(reference)),
    reply: (reference: string, input: { body: string; internal: boolean; statusAfter?: TicketStatus }) =>
      post<{ id: string; direction: string; at: string }>(`${ticket(reference)}/messages`, input),
    /** Save a draft for review, or replace the pending one's text (support#79). */
    saveDraft: (reference: string, body: string) =>
      post<{ id: string; status: TicketStatus; savedAt: string }>(`${ticket(reference)}/drafts`, { body }),
    /** Send the pending draft as the signed-in person; `body` when they edited it. */
    sendDraft: (reference: string, draftId: string, input: { body?: string; statusAfter?: TicketStatus }) =>
      post<{ id: string; direction: string; at: string }>(
        `${ticket(reference)}/drafts/${encodeURIComponent(draftId)}/send`,
        input,
      ),
    discardDraft: (reference: string, draftId: string) =>
      post<{ status: TicketStatus }>(`${ticket(reference)}/drafts/${encodeURIComponent(draftId)}/discard`, {}),
    assign: (reference: string, assigneeUserId: string | null) =>
      post<{ reference: string; assigneeUserId: string | null }>(`${ticket(reference)}/assign`, { assigneeUserId }),
    setStatus: (reference: string, status: TicketStatus) =>
      post<{ reference: string; status: TicketStatus }>(`${ticket(reference)}/status`, { status }),
    setPriority: (reference: string, priority: TicketPriority) =>
      post<{ reference: string; priority: TicketPriority }>(`${ticket(reference)}/priority`, { priority }),
    createTicket: (input: NewTicketBody) => post<{ reference: string; status: TicketStatus }>('/admin/tickets', input),
    products: () => get<{ products: SupportProduct[] }>('/admin/products'),
    setCategory: (reference: string, categoryId: string | null) =>
      post<{ reference: string; category: CategoryLabel | null }>(`${ticket(reference)}/category`, { categoryId }),
    categories: (withArchived = false) => get<CategoryList>(`/admin/categories${withArchived ? '?archived=true' : ''}`),
    createCategory: (input: { name: string; parentId: string | null; products?: string[] }) =>
      post<Category>('/admin/categories', input),
    updateCategory: (
      id: string,
      change: { name?: string; parentId?: string | null; products?: string[]; sortOrder?: number; archived?: boolean },
    ) => patch<Category>(`/admin/categories/${encodeURIComponent(id)}`, change),
    categoryRules: () => get<CategoryRuleOverview>('/admin/category-rules'),
    createCategoryRule: (input: CategoryRuleInput) => post<{ id: string }>('/admin/category-rules', input),
    updateCategoryRule: (id: string, input: CategoryRuleInput) =>
      put<{ id: string }>(`/admin/category-rules/${encodeURIComponent(id)}`, input),
    deleteCategoryRule: (id: string) => del(`/admin/category-rules/${encodeURIComponent(id)}`),
    setSpam: (reference: string, spam: boolean) =>
      post<{ reference: string; flag: TicketFlag }>(`${ticket(reference)}/spam`, { spam }),
    feedback: (query: FeedbackQuery) => get<FeedbackPage>(`/admin/feedback${feedbackSearch(query)}`),
    feedbackItem: (id: string) => get<FeedbackDetail>(fb(id)),
    feedbackAttachment: (id: string, attachmentId: string) =>
      get<{ url: string; filename: string }>(`${fb(id)}/attachments/${encodeURIComponent(attachmentId)}`),
    tagFeedback: (id: string, tags: string[]) => post<ReviewedFeedback>(`${fb(id)}/tags`, { tags }),
    archiveFeedback: (id: string) => post<ReviewedFeedback>(`${fb(id)}/archive`, {}),
    restoreFeedback: (id: string) => post<ReviewedFeedback>(`${fb(id)}/restore`, {}),
    convertFeedback: (id: string, input: { notify: boolean }) =>
      post<{ reference: string; feedback: ReviewedFeedback }>(`${fb(id)}/convert`, input),
    overview: () => get<SupportOverview>('/admin/overview'),
    categoryStats: (parent: string | null) =>
      get<CategoryStats>(`/admin/overview/categories${parent ? `?parent=${encodeURIComponent(parent)}` : ''}`),
    volumeAlert: () => get<VolumeAlertState>('/admin/volume-alert'),
    setVolumeAlert: (input: VolumeAlertThresholds) => put<VolumeAlertState>('/admin/volume-alert', input),
    /** Removes the file and its row for good (auth#672). Support records who and when as an event. */
    deleteAttachment: (reference: string, id: string) =>
      del(`${ticket(reference)}/attachments/${encodeURIComponent(id)}`),
    attachment: (reference: string, id: string) =>
      get<{ url: string; expiresInSeconds: number; filename: string; contentType: string; bytes: number }>(
        `${ticket(reference)}/attachments/${encodeURIComponent(id)}`,
      ),
  };
}

export type SupportApi = ReturnType<typeof supportApi>;

// ── Feedback review and the overview (org-admin#140) ───────────────────

export type FeedbackState = 'UNREVIEWED' | 'TAGGED' | 'CONVERTED' | 'ARCHIVED';
export const FEEDBACK_STATES: readonly FeedbackState[] = ['UNREVIEWED', 'TAGGED', 'CONVERTED', 'ARCHIVED'];
/** UNCLASSIFIED is email to feedback@ (support#83): the sender picked no kind. */
export type FeedbackKind = 'IDEA' | 'PRAISE' | 'COMPLAINT' | 'BUG' | 'UNCLASSIFIED';
export const FEEDBACK_KINDS: readonly FeedbackKind[] = ['IDEA', 'BUG', 'COMPLAINT', 'PRAISE', 'UNCLASSIFIED'];

export const FEEDBACK_STATE_LABELS: Record<FeedbackState, string> = {
  UNREVIEWED: 'Unreviewed',
  TAGGED: 'Tagged',
  CONVERTED: 'Made a ticket',
  ARCHIVED: 'Archived',
};
export const FEEDBACK_KIND_LABELS: Record<FeedbackKind, string> = {
  IDEA: 'Idea',
  BUG: 'Bug',
  COMPLAINT: 'Complaint',
  PRAISE: 'Praise',
  UNCLASSIFIED: 'Unclassified',
};

/**
 * Support names the sender only when they said we may contact them; otherwise
 * there is no id and no address to show, by design.
 */
export type FeedbackSubmitter =
  | { mayContact: true; userId: string | null; email: string | null }
  | { mayContact: false };

/** A file on feedback or a ticket, as staff see it. */
export interface StaffAttachment {
  id: string;
  filename: string;
  contentType: string;
  bytes: number;
  isDiagnosticLog: boolean;
  expired?: boolean;
}

export interface ReviewedFeedback {
  id: string;
  product: string;
  kind: FeedbackKind;
  state: FeedbackState;
  channel: string;
  message: string;
  createdAt: string;
  tags: string[];
  ticketReference: string | null;
  submitter: FeedbackSubmitter;
  /** The screenshot sent with it, if any (keyodo#562). Optional for an older support. */
  attachments?: StaffAttachment[];
}

/** One piece of feedback with what the detail screen shows around it (support#45). */
export interface FeedbackDetail extends ReviewedFeedback {
  related?: {
    /** Null unless the sender allowed contact: counting their history identifies them. */
    sender: { feedback: number; tickets: number } | null;
    similar: {
      count: number;
      items: { id: string; kind: FeedbackKind; product: string; preview: string; createdAt: string }[];
    };
  };
}

export interface FeedbackPage {
  feedback: ReviewedFeedback[];
  hasMore: boolean;
  nextCursor: string | null;
  counts: {
    state: Record<FeedbackState, number>;
    product: { product: string; count: number }[];
    tag: { name: string; count: number }[];
  };
  weeks: Record<FeedbackKind, { thisWeek: number; lastWeek: number }>;
}

export interface FeedbackQuery {
  state: FeedbackState;
  product?: string;
  kind?: FeedbackKind;
  tag?: string;
  cursor?: string;
}

export function feedbackSearch(query: FeedbackQuery): string {
  const params = new URLSearchParams({ state: query.state });
  if (query.product) params.set('product', query.product);
  if (query.kind) params.set('kind', query.kind);
  if (query.tag) params.set('tag', query.tag);
  if (query.cursor) params.set('cursor', query.cursor);
  return `?${params}`;
}

/** Tags as typed into the box: comma separated, blanks dropped. Support normalises the rest. */
export function parseTags(text: string): string[] {
  return text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

export interface SupportOverview {
  window: { from: string; to: string };
  open: { open: number; waitingOnUs: number; waitingOnUser: number };
  firstResponse: { medianMs: number | null; priorMedianMs: number | null; answered: number };
  oldestUnanswered: { reference: string; subject: string; assigneeUserId: string | null; waitingSince: string } | null;
  volumeByProduct: { product: string; count: number }[];
  newTickets: number;
  topTags: { name: string; count: number }[];
  resolved: {
    count: number;
    priorCount: number;
    medianMs: number | null;
    byAssignee: { assigneeUserId: string | null; count: number }[];
  };
  spam: number;
}

/**
 * Tickets by category on the Overview (support#68 phase 3): one level of the
 * tree, each row counting its whole branch. `weekly` is twelve rolling weeks,
 * oldest first. Response times are on tickets opened in those twelve weeks.
 */
export interface CategoryStatsRow {
  id: string | null;
  kind: 'category' | 'direct' | 'none';
  name: string;
  archived: boolean;
  hasChildren: boolean;
  newThisWeek: number;
  newPriorWeek: number;
  weekly: number[];
  openNow: number;
  medianFirstResponseMs: number | null;
  medianResolveMs: number | null;
  reopened: number;
}

export interface CategoryStats {
  window: { from: string; to: string; weeks: number };
  parent: { id: string; name: string; trail: string[] } | null;
  totals: { newThisWeek: number; categorizedThisWeek: number; needsCategoryOpen: number };
  rows: CategoryStatsRow[];
}

/** A span of time as a person says it: `45m`, `7h 20m`, `2d 4h`. Null reads as a dash. */
export function formatSpan(ms: number | null): string {
  if (ms === null) return '–';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
}

// ── Opening a ticket as staff ──────────────────────────────────────────

/** A registered application, as support reports it for the New ticket form. */
export interface SupportProduct {
  slug: string;
  name: string;
  /** Letters only, or null for the platform's `RW`. */
  ticketPrefix: string | null;
}

/**
 * `platform` is not an application, so it is offered here rather than by
 * support, and first: staff mostly open a ticket for a problem in the platform
 * itself (Jon, 2026-09-27). It files as `RW-`.
 */
export const PLATFORM_PRODUCT = { value: 'platform', label: 'Platform (RW-)' };

/**
 * The product choices, from the registered applications (Jon, 2026-09-27:
 * prefixes are set on the application). Each label shows the prefix its
 * tickets will get, so the form says what the reference will look like.
 */
export function productOptions(products: readonly SupportProduct[]): { value: string; label: string }[] {
  return [
    PLATFORM_PRODUCT,
    ...products
      .filter((p) => p.slug !== PLATFORM_PRODUCT.value)
      .map((p) => ({ value: p.slug, label: `${p.name} (${p.ticketPrefix ?? 'RW'}-)` })),
  ];
}

export type NewTicketBody = {
  subject: string;
  product: string;
  requester: { userId: string } | { email: string };
  description?: string;
  internalNote?: string;
  priority: TicketPriority;
  notify: boolean;
};

export interface NewTicketForm {
  subject: string;
  product: string;
  priority: TicketPriority;
  /** `me`: the signed-in staff member. The default, and the common case. */
  requesterKind: 'me' | 'account' | 'email';
  me: string | undefined;
  account: { id: string } | null;
  email: string;
  description: string;
  internalNote: string;
  notify: boolean;
}

/**
 * The form, as the request support expects, or what is missing.
 *
 * Mirrors support's own validation (subject of 3 or more, a requester, and a
 * description or a note) so the Create button can say why it is not ready
 * rather than sending something support will refuse. Support still checks;
 * this is for the words on the screen.
 */
export function newTicketBody(f: NewTicketForm): { body: NewTicketBody } | { missing: string } {
  const subject = f.subject.trim();
  if (subject.length < 3) return { missing: 'A subject of at least 3 characters.' };

  let requester: NewTicketBody['requester'];
  if (f.requesterKind === 'me') {
    if (!f.me) return { missing: 'Your own account, which has not loaded yet.' };
    requester = { userId: f.me };
  } else if (f.requesterKind === 'account') {
    if (!f.account) return { missing: 'An account: search, then pick one.' };
    requester = { userId: f.account.id };
  } else {
    const email = f.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return { missing: 'A valid email address.' };
    requester = { email };
  }

  const description = f.description.trim();
  const internalNote = f.internalNote.trim();
  if (!description && !internalNote) return { missing: 'A description, an internal note, or both.' };

  return {
    body: {
      subject,
      product: f.product,
      requester,
      ...(description ? { description } : {}),
      ...(internalNote ? { internalNote } : {}),
      priority: f.priority,
      notify: f.notify,
    },
  };
}

// ── Words, one place ────────────────────────────────────────────────────

/**
 * What to call a member of staff: their name, or their address when they
 * have none. Null when auth knows neither, so the caller shows the short id
 * rather than a blank.
 */
export function personName(u: { name?: string | null; email?: string | null }): string | null {
  return u.name?.trim() || u.email || null;
}

export const VIEW_LABELS: Record<SavedView, string> = {
  'all-open': 'All open',
  unassigned: 'Unassigned',
  'waiting-on-us': 'Waiting on us',
  'waiting-on-user': 'Waiting on them',
  'waiting-for-review': 'Waiting for review',
  'needs-category': 'Needs category',
  'resolved-today': 'Resolved today',
  spam: 'Spam',
};

export const STATUS_LABELS: Record<TicketStatus, string> = {
  OPEN: 'Open',
  WAITING_ON_SUPPORT: 'Waiting on us',
  WAITING_ON_USER: 'Waiting on them',
  RESOLVED: 'Resolved',
  WAITING_FOR_REVIEW: 'Waiting for review',
};

export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  URGENT: 'Urgent',
  HIGH: 'High',
  NORMAL: 'Normal',
  LOW: 'Low',
};

/** Badge tone for a status: whose move it is decides the colour. */
export function statusTone(status: TicketStatus): 'warn' | 'neutral' | 'good' {
  if (status === 'OPEN' || status === 'WAITING_ON_SUPPORT' || status === 'WAITING_FOR_REVIEW') return 'warn';
  if (status === 'RESOLVED') return 'good';
  return 'neutral';
}

/**
 * The event trail, in words. Kinds support writes today; anything else is
 * shown by its kind rather than dropped, because an event nobody can read is
 * still an event somebody may need.
 */
export function describeEvent(e: Pick<TicketEvent, 'kind' | 'detail'>): string {
  const d = (e.detail ?? {}) as Record<string, unknown>;
  const pair = (map?: Record<string, string>) =>
    typeof d.from === 'string' && typeof d.to === 'string'
      ? ` from ${map?.[d.from] ?? d.from} to ${map?.[d.to] ?? d.to}`
      : '';
  switch (e.kind) {
    case 'CREATED':
      return 'Opened';
    case 'STATUS_CHANGED':
      return `Status changed${pair(STATUS_LABELS)}`;
    case 'PRIORITY_CHANGED':
      return `Priority changed${pair(PRIORITY_LABELS)}`;
    case 'ASSIGNED':
      return 'Assigned';
    case 'UNASSIGNED':
      return 'Unassigned';
    case 'MARKED_SPAM':
      return 'Marked as spam';
    case 'UNMARKED_SPAM':
      return 'Spam mark removed';
    case 'REOPENED':
      return 'Reopened by a reply';
    case 'RESOLVED':
      return 'Resolved';
    case 'NOTIFIED':
      return 'Email sent';
    case 'NOTIFICATION_FAILED':
      return 'Email could not be sent';
    case 'NOTIFICATION_SKIPPED':
      return 'No email sent: the requester closed their account';
    case 'IDENTITY_CLOSED':
      return 'The requester closed their account; their details were removed';
    /**
     * An unsigned email added to this ticket rather than opening a new one
     * (support#60): the sender already had this one open, or had opened their
     * one ticket for the day.
     */
    /** support#68: `{ from, to }` are `{ id, name }` as they were named at the time, or null. */
    case 'CATEGORY_CHANGED': {
      const name = (v: unknown) =>
        v && typeof v === 'object' && typeof (v as { name?: unknown }).name === 'string' ? (v as { name: string }).name : null;
      const to = name(d.to);
      const from = name(d.from);
      const who = d.by === 'rule' ? ' automatically' : '';
      if (!to) return `Category cleared${from ? ` (was ${from})` : ''}`;
      return from ? `Category changed from ${from} to ${to}${who}` : `Filed under ${to}${who}`;
    }
    /** support#79: a draft reply, saved, sent by a person, or discarded. */
    case 'DRAFT_SAVED':
      return d.replaced ? 'Draft for review replaced' : 'Draft saved for review';
    case 'DRAFT_SENT':
      return d.edited ? 'Draft edited and sent' : 'Draft sent';
    case 'DRAFT_DISCARDED':
      if (d.reason === 'SUPERSEDED') return 'Draft discarded: a reply was sent instead';
      if (d.reason === 'STATUS_CHANGED') return 'Draft discarded: the status was changed';
      return 'Draft discarded';
    /** auth#672: staff deleted an attachment, usually an ID once the age was verified. */
    case 'ATTACHMENT_DELETED':
      return typeof d.filename === 'string' ? `Attachment deleted: ${d.filename}` : 'Attachment deleted';
    case 'MESSAGE_FOLDED':
      return d.reason === 'DAILY_LIMIT'
        ? 'Email added here: the sender had already opened a request today'
        : 'Email added here: the sender already had this request open';
    default:
      return e.kind;
  }
}

// ── The ticket-volume alert (support#60) ───────────────────────────────

/** `null` switches that window off. */
export interface VolumeAlertThresholds {
  perHour: number | null;
  perDay: number | null;
}

export interface VolumeAlertState extends VolumeAlertThresholds {
  updatedAt: string | null;
  updatedByUserId: string | null;
  /** The last alerts that fired, newest first. */
  recent: {
    window: 'HOUR' | 'DAY';
    threshold: number;
    count: number;
    recipients: number;
    failed: number;
    sentAt: string;
  }[];
}

/** The support service's range for a threshold. */
export const THRESHOLD_MAX = 100_000;

/**
 * Read one threshold box. Blank means off, a real choice; anything that is not
 * a whole number from 1 to `THRESHOLD_MAX` is `'invalid'`, never off, so a typo
 * cannot quietly switch the alert off.
 */
export function parseThreshold(text: string): number | null | 'invalid' {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d+$/.test(t)) return 'invalid';
  const n = Number(t);
  return n >= 1 && n <= THRESHOLD_MAX ? n : 'invalid';
}
